/**
 * PDF.js 2.16.105 UMD, loaded from same-origin files under public/pdfjs/.
 * Avoids CDN/CSP blocks (fixes "Failed to load PDF.js script" in locked-down networks).
 *
 * Bundled files: public/pdfjs/pdf.min.js, public/pdfjs/pdf.worker.min.js
 * To upgrade: replace both from https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/
 */
const PDFJS_VERSION = "2.16.105";

function bundleBasePath() {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/pdfjs`;
}

export function getPdfJsScriptUrl() {
  return `${bundleBasePath()}/pdf.min.js`;
}

export function getPdfJsWorkerUrl(bundleScriptOrigin) {
  const path = `${bundleBasePath()}/pdf.worker.min.js`;
  if (bundleScriptOrigin) {
    try {
      return new URL(path, bundleScriptOrigin).href;
    } catch {
      /* fall through */
    }
  }
  return path;
}

let loadPromise = null;

export function ensurePdfJsLoaded() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("PDF.js requires a browser environment"));
  }
  const PDFJS_SCRIPT = getPdfJsScriptUrl();
  const PDFJS_WORKER = getPdfJsWorkerUrl(window.location.origin);

  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    return Promise.resolve(window.pdfjsLib);
  }
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = new Promise((resolve, reject) => {
    const selector = `script[data-pdfjs-bundle="${PDFJS_VERSION}"]`;
    const existing = document.querySelector(selector);
    const onFail = () => {
      loadPromise = null;
      reject(
        new Error(
          `Failed to load PDF.js from ${PDFJS_SCRIPT}. Ensure public/pdfjs/ is deployed and not blocked.`
        )
      );
    };
    if (existing) {
      if (window.pdfjsLib) {
        finish(resolve, reject, PDFJS_WORKER);
        return;
      }
      existing.addEventListener("load", () => finish(resolve, reject, PDFJS_WORKER));
      existing.addEventListener("error", onFail);
      return;
    }
    const script = document.createElement("script");
    script.src = PDFJS_SCRIPT;
    script.async = true;
    script.dataset.pdfjsBundle = PDFJS_VERSION;
    script.onload = () => finish(resolve, reject, PDFJS_WORKER);
    script.onerror = onFail;
    document.head.appendChild(script);
  });
  return loadPromise;
}

function finish(resolve, reject, workerSrc) {
  const lib = window.pdfjsLib;
  if (!lib || typeof lib.getDocument !== "function") {
    loadPromise = null;
    reject(new Error("PDF.js loaded but pdfjsLib is missing"));
    return;
  }
  lib.GlobalWorkerOptions.workerSrc = workerSrc;
  resolve(lib);
}
